import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { fetchBySlug, getLocalized } from "../lib/cms";
import { CONTACT_INFO } from "../data";
import ContactForm from "../components/ContactForm";

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadKontakt() {
      try {
        const data = await fetchBySlug("kontakt");
        setPageData(data);
      } catch (err) {
        console.error("Fejl ved hentning af kontakt-data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadKontakt();
  }, []);

  const title = pageData ? getLocalized(pageData.title) : "Kontakt";
  const contentHtml = pageData ? getLocalized(pageData.content) : "";
  const metaTitle = pageData ? getLocalized(pageData.meta_title) : "Kontakt | Bachmann Tømrer";
  const metaDesc = pageData ? getLocalized(pageData.meta_description) : "Har du et projekt eller spørgsmål? Kontakt Bachmann Tømrer i dag for et uforpligtende tilbud på tømrerarbejde i Silkeborg, Ry og Kjellerup.";

  return (
    <div className="animate-in fade-in duration-500">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      {/* Page Hero */}
      <section className="relative py-24 overflow-hidden bg-brand-graphite text-white">
        {/* Background Image with Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${pageData?.images?.[0] || 'https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/image00038-scaled.jpeg'})`,
          }}
        />
        <div className="absolute inset-0 bg-brand-graphite/75" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-beige">Kontakt os i dag</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2">
            {title}
          </h1>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
            <Link to="/" className="hover:text-brand-beige transition-colors">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[8px]" />
            <span className="text-white font-medium">{title}</span>
          </div>
        </div>
      </section>

      {/* Contact Content & Form Grid */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Left Column: Contact Info & CMS Content */}
            <div className="lg:col-span-5 space-y-8">
              {loading ? (
                <div className="flex py-12">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-brown border-t-transparent" />
                </div>
              ) : pageData ? (
                <div 
                  className="prose prose-brand max-w-none text-brand-graphite/80 leading-relaxed text-sm sm:text-base space-y-6"
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              ) : (
                <div className="space-y-4">
                  <h2 className="font-serif text-2xl font-bold text-brand-graphite">
                    Lad os høre om dit projekt
                  </h2>
                  <p className="text-xs text-brand-graphite/70">
                    Vi står klar til at hjælpe dig med professionelt tømrerarbejde i Silkeborg og omegn.
                  </p>
                </div>
              )}
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed (Optional, but adds high professional value) */}
      <section className="h-96 w-full border-t border-brand-brown/10 bg-brand-beige-dark/20 overflow-hidden">
        <div className="w-full h-full grayscale hover:grayscale-0 transition-all duration-500 ease-in-out">
          <iframe
            title="Bachmann Tømrer Lokation"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2222.846549553757!2d9.516599177372223!3d56.19445197325244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464bf00473d09221%3A0x8386341f23859625!2sSk%C3%A6gk%C3%A6rv%C3%A6nget%2023%2C%208600%20Silkeborg!5e0!3m2!1sda!2sdk!4v1718530000000!5m2!1sda!2sdk"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}
